import { msg } from "@lingui/core/macro";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError, isKnownErrorCode } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";

import { useLingui } from "@lingui/react/macro";
import {
  approveInvoicingOccurrences,
  invoicingOccurrencesAndGroupsOptions,
  invoicingOccurrencesAndGroupsToApproveOptions,
} from "@/api/Invoicing";
import {
  manualInvoicingEnabled,
  organizationUsesInvoicingCodes,
} from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import { getDataFromInvoicingOccurrencesAndGroups } from "./getReportingTableData";
import styles from "./ReportingTables.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { Trans } from "@lingui/react/macro";
import { FilledButton } from "@medoma/components/Button/Button";
import NoResults from "@/components/NoResults/NoResults";
import type {
  IInvoicingActivityOccurrenceOrGroup,
  IInvoicingCode,
} from "@medoma/models/invoicing";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import type { MessageDescriptor } from "@lingui/core";
import {
  formatPersonalIdentityNumber,
  patientTypeDictionary,
} from "@medoma/models/patients";
import { medicalCompetenceDictionary } from "@medoma/models/shifts";
import { Time } from "@/components/Time/Time";
import { Link } from "@medoma/components/Link/Link";
import { NotExecutedActivityOccurrencePopover } from "./NotExecutedActivityOccurrencePopover/NotExecutedActivityOccurrencePopover";
import { Actions } from "./Actions/Actions";
import { useCallback, useId } from "react";
import ShieldCheckIcon from "@medoma/components/icons/ShieldCheckIcon";
import { humanFormat } from "@medoma/models/date-and-time";
import { isGroup } from "@medoma/models/activities";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { useSortState } from "@/components/SortableColumnHeader/useSortState";
import { SortableColumnHeader } from "@/components/SortableColumnHeader/SortableColumnHeader";
import { useSortedItems } from "@/components/SortableColumnHeader/useSortedItems";
import { Text } from "@medoma/components/Text/Text";

const getOccurrenceIds = (
  occurrencesAndGroups: IInvoicingActivityOccurrenceOrGroup[],
) => {
  return occurrencesAndGroups
    .map((occurrenceOrGroup) => {
      if (isGroup(occurrenceOrGroup)) {
        return occurrenceOrGroup.occurrences.map(
          (occurrence) => occurrence.occurrenceId,
        );
      } else {
        return occurrenceOrGroup.occurrenceId;
      }
    })
    .flat();
};

const columns: {
  name: MessageDescriptor;
  id: keyof ReturnType<typeof getDataFromInvoicingOccurrencesAndGroups>[0];
  isRowHeader?: true;
  isInvoicingCode?: true;
}[] = [
  { name: msg`Namn`, id: "patient", isRowHeader: true },
  { name: msg`Pers.nr`, id: "personalIdentityNumber" },
  {
    name: msg`Patienttyp`,
    id: "patientType",
  },
  {
    name: msg`Kompetenser`,
    id: "competences",
  },
  { name: msg`Utförandetid`, id: "finishedAt" },
  {
    name: msg`Aktivitet`,
    id: "title",
  },
  {
    name: msg`Diagnoskod`,
    id: "diagnosisCode",
  },
  {
    name: msg`KVÅ-kod`,
    id: "procedureCodes",
    isInvoicingCode: true,
  },
  { id: "productCodes", name: msg`Produktkod`, isInvoicingCode: true },
  {
    name: msg`Kommentar`,
    id: "comment",
  },
  {
    name: msg`Åtgärder`,
    id: "actions",
  },
];

const hasInvoicingCode = ({
  procedureCodes,
  productCodes,
}: {
  procedureCodes: IInvoicingCode[];
  productCodes: IInvoicingCode[];
}) => {
  if (procedureCodes.length >= 1 || productCodes.length >= 1) {
    return true;
  }
  return false;
};

const formatCell =
  (translator: ReturnType<typeof useLingui>["t"]) =>
  // This rule is just for simplifying debugging, OK to skip
  // oxlint-disable-next-line react/display-name
  (
    item: ReturnType<typeof getDataFromInvoicingOccurrencesAndGroups>[0],
    key: keyof ReturnType<typeof getDataFromInvoicingOccurrencesAndGroups>[0],
  ) => {
    switch (key) {
      case "patient": {
        const { id: patientId, name: patientName, paymentExempt } = item[key];
        return (
          <div className={styles.patient}>
            <Link to={`/commandcenter/patients/${patientId}`} weight="regular">
              {patientName}
            </Link>
            {paymentExempt ? (
              <div className={styles.paymentExempt}>
                <ShieldCheckIcon />
                <Trans>Betalningsbefriad</Trans>
              </div>
            ) : null}
          </div>
        );
      }
      case "personalIdentityNumber": {
        const { number, type } = item[key];
        return formatPersonalIdentityNumber({
          personalIdentityNumber: number,
          personalIdentityNumberType: type,
        });
      }
      case "patientType":
        return translator(patientTypeDictionary[item[key]]);
      case "competences": {
        const competences = item[key];
        // MED-5101: Always sort the CareGiverType list
        return [...competences]
          .sort((competenceA, competenceB) => {
            if (competenceA === "MedicalDoctor") {
              return -1;
            }
            if (competenceB === "MedicalDoctor") {
              return 1;
            }
            return 0;
          })
          .map((competence) =>
            translator(medicalCompetenceDictionary[competence].short),
          )
          .join(", ");
      }
      case "finishedAt":
        return item[key] ? (
          <Time color="default">
            {humanFormat(item[key], { dateStyle: "short", timeStyle: "short" })}
          </Time>
        ) : null;
      case "title": {
        const occurrences = item[key];
        // A batch always shares a single category, so the first occurrence's
        // activity type represents the whole cell.
        const category = occurrences[0]?.activityType;
        return (
          <div className={styles.activityOccurrences}>
            {category ? <CategoryIcon category={category} size="tiny" /> : null}
            <div className={styles.activityOccurrenceList}>
              {occurrences.map(
                ({
                  activityId,
                  occurrenceId,
                  title,
                  status,
                  finishedAt,
                  procedureCodes,
                  productCodes,
                }) => (
                  <div
                    key={`${activityId}-${occurrenceId}`}
                    className={styles.activityOccurrence}
                  >
                    <Link
                      to={`activities/${activityId}/occurrences/${occurrenceId}`}
                      weight="regular"
                    >
                      {finishedAt ? (
                        <span>{title}</span>
                      ) : (
                        <div className={styles.occurrenceNotPerformed}>
                          <span>{title}</span>
                        </div>
                      )}
                    </Link>
                    {status === "notExecuted" &&
                    hasInvoicingCode({ procedureCodes, productCodes }) ? (
                      <NotExecutedActivityOccurrencePopover
                        activityId={activityId}
                        occurrenceId={occurrenceId}
                        title={title}
                      />
                    ) : null}
                  </div>
                ),
              )}
            </div>
          </div>
        );
      }
      case "diagnosisCode":
        return item[key];
      case "procedureCodes":
        return item[key].map((procedureCode) => (
          <div
            key={procedureCode.code}
          >{`${procedureCode.code} - ${procedureCode.name}`}</div>
        ));
      case "productCodes":
        return item[key].map((productCode) => (
          <div
            key={productCode.code}
          >{`${productCode.code} - ${productCode.name}`}</div>
        ));
      case "comment": {
        const error = item[key];
        if (!error) {
          return "";
        }
        if (isKnownErrorCode(error)) {
          return deducedError(error);
        }
        return error;
      }
      case "actions":
        return (
          <div className={styles.actions}>
            <Actions activityOccurrenceOrGroupId={item[key]} />
          </div>
        );
      default:
        return key satisfies never;
    }
  };

type ReportingTableRow = ReturnType<
  typeof getDataFromInvoicingOccurrencesAndGroups
>[0];

const sortableColumnIds = [
  "patient",
  "personalIdentityNumber",
  "patientType",
  "finishedAt",
  "diagnosisCode",
] as const;

type SortableColumnId = (typeof sortableColumnIds)[number];

const isSortableColumn = (
  id: keyof ReportingTableRow,
): id is SortableColumnId =>
  (sortableColumnIds as readonly string[]).includes(id);

const ReportingTable = ({
  headingId,
  rows,
}: {
  headingId: string;
  rows: ReportingTableRow[];
}) => {
  const { t } = useLingui();
  const { organization } = useUserProfile();
  const { sortState, toggleSort } = useSortState<SortableColumnId>({
    column: "patient",
    direction: "asc",
  });
  const visibleColumns = organizationUsesInvoicingCodes(organization)
    ? columns
    : columns.filter((column) => !column.isInvoicingCode);
  const compareRows = useCallback(
    (a: ReportingTableRow, b: ReportingTableRow, column: SortableColumnId) => {
      switch (column) {
        case "patient":
          return a.patient.name.localeCompare(b.patient.name);
        case "personalIdentityNumber":
          return a.personalIdentityNumber.number.localeCompare(
            b.personalIdentityNumber.number,
          );
        case "patientType":
          return t(patientTypeDictionary[a.patientType]).localeCompare(
            t(patientTypeDictionary[b.patientType]),
          );
        case "finishedAt": {
          const timeA = a.finishedAt?.getTime() ?? 0;
          const timeB = b.finishedAt?.getTime() ?? 0;
          return timeA - timeB;
        }
        case "diagnosisCode":
          return (a.diagnosisCode ?? "").localeCompare(b.diagnosisCode ?? "");
      }
    },
    [t],
  );
  const sortedRows = useSortedItems(rows, sortState, compareRows);
  return (
    <Table className={styles.reportingTable} aria-labelledby={headingId}>
      <TableHeader columns={visibleColumns} dependencies={[sortState]}>
        {(column) => (
          <Column id={column.id} isRowHeader={column.isRowHeader}>
            {isSortableColumn(column.id) ? (
              <SortableColumnHeader
                columnId={column.id}
                sortState={sortState}
                toggleSort={toggleSort}
              >
                {t(column.name)}
              </SortableColumnHeader>
            ) : (
              <Text element="span" weight="medium" size="small">
                {t(column.name)}
              </Text>
            )}
          </Column>
        )}
      </TableHeader>
      <TableBody items={sortedRows}>
        {(item) => {
          return (
            <Row id={item.title[0]?.occurrenceId} columns={visibleColumns}>
              {(column) => <Cell>{formatCell(t)(item, column.id)}</Cell>}
            </Row>
          );
        }}
      </TableBody>
    </Table>
  );
};

export const PendingTable = () => {
  const headingId = useId();
  const { t } = useLingui();
  const {
    data: invoicingOccurrencesAndGroups,
    isError,
    isPending,
    error,
  } = useQuery(
    invoicingOccurrencesAndGroupsOptions({
      invoicingStatuses: ["pending"],
    }),
  );

  if (isError) {
    Sentry.captureException(error);
  }

  const noData = invoicingOccurrencesAndGroups?.length === 0;

  // `rows` are not rendered until `isPending` or `isError` is resolved, so this is safe
  const rows = isPending
    ? []
    : isError
      ? []
      : getDataFromInvoicingOccurrencesAndGroups(invoicingOccurrencesAndGroups);

  return (
    <div className={styles.tableContainer}>
      {isPending ? (
        <div className={styles.systemMessage}>
          <Loading message={t`Hämtar underlag för rapportering`} />
        </div>
      ) : isError ? (
        <div className={styles.systemMessage}>
          <ErrorMessage
            message={`${t`Kunde inte hämta underlag för rapportering.`} ${deducedError(error)}`}
          />
        </div>
      ) : noData ? (
        <div className={styles.systemMessage}>
          <NoResults message={t`Inget underlag att visa`} />
        </div>
      ) : (
        <ReportingTable headingId={headingId} rows={rows} />
      )}
    </div>
  );
};

const ErrorTable = () => {
  const headingId = useId();
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const {
    data: invoicingOccurrencesAndGroups,
    isError,
    isPending,
    error,
  } = useQuery(
    invoicingOccurrencesAndGroupsOptions({
      invoicingStatuses: ["error"],
    }),
  );

  if (isError) {
    Sentry.captureException(error);
  }

  const shouldNotShowInvoicingApprovalButton =
    !invoicingOccurrencesAndGroups ||
    invoicingOccurrencesAndGroups.length === 0;

  const {
    mutate: mutateApproveInvoicingOccurrences,
    isPending: isPendingApprove,
  } = useMutation({
    mutationFn: (
      invoicingOccurrencesAndGroups: IInvoicingActivityOccurrenceOrGroup[],
    ) =>
      approveInvoicingOccurrences(
        getOccurrenceIds(invoicingOccurrencesAndGroups),
      ),
    onSuccess: async () => {
      return queryClient.invalidateQueries();
    },
  });

  // `rows` are not rendered until `isPending` or `isError` is resolved, so this is safe
  const rows = isPending
    ? []
    : isError
      ? []
      : getDataFromInvoicingOccurrencesAndGroups(invoicingOccurrencesAndGroups);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeading}>
        <Heading level="h2" id={headingId}>
          <Trans>Avvisade</Trans>
        </Heading>
        {shouldNotShowInvoicingApprovalButton ? null : (
          <FilledButton
            size="small"
            onClick={() =>
              mutateApproveInvoicingOccurrences(invoicingOccurrencesAndGroups)
            }
            disabled={isPendingApprove}
          >
            {isPendingApprove ? (
              <Loading message={t`Godkänner`} />
            ) : (
              <Trans>Godkänn och skicka till GVR</Trans>
            )}
          </FilledButton>
        )}
      </div>
      {isPending ? (
        <div className={styles.systemMessage}>
          <Loading message={t`Hämtar underlag för rapportering`} />
        </div>
      ) : isError ? (
        <div className={styles.systemMessage}>
          <ErrorMessage
            message={`${t`Kunde inte hämta underlag för rapportering.`} ${deducedError(error)}`}
          />
        </div>
      ) : shouldNotShowInvoicingApprovalButton ? (
        <div className={styles.systemMessage}>
          <NoResults message={t`Inget underlag att visa`} />
        </div>
      ) : (
        <ReportingTable headingId={headingId} rows={rows} />
      )}
    </div>
  );
};

const AwaitingApprovalTable = () => {
  const headingId = useId();
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { organization } = useUserProfile();

  const {
    data: invoicingOccurrencesAndGroups,
    isError,
    isPending,
    error,
  } = useQuery({
    ...invoicingOccurrencesAndGroupsToApproveOptions,
    // This is used to achieve somewhat responsive data, despite async flows in the backend
    refetchInterval: 5000,
  });

  if (isError) {
    Sentry.captureException(error);
  }

  const shouldNotShowInvoicingApprovalButton =
    !invoicingOccurrencesAndGroups ||
    invoicingOccurrencesAndGroups.length === 0;
  const {
    mutate: mutateApproveInvoicingOccurrences,
    isPending: isPendingApprove,
  } = useMutation({
    mutationFn: (
      invoicingOccurrencesAndGroups: IInvoicingActivityOccurrenceOrGroup[],
    ) =>
      approveInvoicingOccurrences(
        getOccurrenceIds(invoicingOccurrencesAndGroups),
      ),
    onSuccess: async () => {
      return queryClient.invalidateQueries();
    },
  });

  // `rows` are not rendered until `isPending` or `isError` is resolved, so this is safe
  const rows = isPending
    ? []
    : isError
      ? []
      : getDataFromInvoicingOccurrencesAndGroups(invoicingOccurrencesAndGroups);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeading}>
        <Heading level="h2">
          <Trans>Att godkänna</Trans>
        </Heading>
        {shouldNotShowInvoicingApprovalButton ||
        manualInvoicingEnabled(organization) ? null : (
          <FilledButton
            size="small"
            onClick={() =>
              mutateApproveInvoicingOccurrences(invoicingOccurrencesAndGroups)
            }
            disabled={isPendingApprove}
          >
            {isPendingApprove ? (
              <Loading message={t`Godkänner...`} />
            ) : (
              <Trans>Godkänn och skicka till GVR</Trans>
            )}
          </FilledButton>
        )}
      </div>
      {isPending ? (
        <div className={styles.systemMessage}>
          <Loading message={t`Hämtar underlag för rapportering`} />
        </div>
      ) : isError ? (
        <div className={styles.systemMessage}>
          <ErrorMessage
            message={`${t`Kunde inte hämta underlag för rapportering.`} ${deducedError(error)}`}
          />
        </div>
      ) : shouldNotShowInvoicingApprovalButton ? (
        <div className={styles.systemMessage}>
          <NoResults message={t`Inget underlag att visa`} />
        </div>
      ) : (
        <ReportingTable headingId={headingId} rows={rows} />
      )}
    </div>
  );
};

const ToHandleTable = () => {
  const { organization } = useUserProfile();
  return (
    <>
      {!manualInvoicingEnabled(organization) ? <ErrorTable /> : null}
      <AwaitingApprovalTable />
    </>
  );
};

export { ToHandleTable };
